/**
 * Sugar Cart Drawer
 * Main custom element for the cart drawer functionality
 */

class SugarCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.testMode = this.dataset.testMode === 'true';
    this.sectionId = this.dataset.sectionId;
    this.container = null;
    this.panel = null;
    this.cartUpdateRequestId = 0;
    this.openFinalizeTimeout = null;
    this.handleOpenTransitionEnd = null;

    // Bind methods
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onContainerClick = this.onContainerClick.bind(this);
  }

  connectedCallback() {
    if (window.__swOk === false) { this.removeAttribute('open'); this.style.display = 'none'; return; }
    this.refreshElementRefs();

    // Set up event listeners
    // Note: keydown listener is added/removed in open/close methods to avoid listening when closed
    // Use container click with event delegation for overlay clicks (more reliable than direct overlay listener)
    if (this.container) {
      this.container.removeEventListener('click', this.onContainerClick);
      this.container.addEventListener('click', this.onContainerClick);
    }

    // Set up cart icon click handler
    this.setHeaderCartIconAccessibility();

    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const source = event?.source;

      // Skip if sugar-cart-items triggered the update (it handles its own re-render)
      // Skip if product-form triggered the update (it calls renderContents directly)
      // Skip sticky-product/sticky-navigation because they already run their own drawer refresh.
      if (
        source === 'sugar-cart-items' ||
        source === 'product-form' ||
        source === 'sticky-product' ||
        source === 'sticky-navigation'
      ) return;
      this.onCartUpdate(event);
    });

    // Initialize any enhancements inside the drawer (slider/countdown, etc.)
    initSugarCartDrawerEnhancements(this);


    const cartReturnUrl = new URL(window.location.href);
    if (cartReturnUrl.searchParams.get('bumpwell_cart') === 'open' && !Shopify.designMode) {
      const openReturnedCart = () => {
        this.open(document.querySelector('.header__icon--cart'));
        cartReturnUrl.searchParams.delete('bumpwell_cart');
        window.history.replaceState(window.history.state, '', cartReturnUrl.pathname + cartReturnUrl.search + cartReturnUrl.hash);
      };
      if (document.readyState === 'complete') {
        requestAnimationFrame(openReturnedCart);
      } else {
        window.addEventListener('load', openReturnedCart, { once: true });
      }
    }

    // Test mode - keep drawer open
    if (this.testMode && Shopify.designMode) {
      setTimeout(() => this.open(), 100);
    }
  }

  disconnectedCallback() {
    this.cartUpdateRequestId += 1;

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.container) {
      this.container.removeEventListener('click', this.onContainerClick);
    }

    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    // Clean up document-level listener if drawer was open when disconnected
    document.removeEventListener('keydown', this.onKeyUp);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.header__icon--cart');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  onKeyUp(event) {
    if (event.code === 'Escape' && this.classList.contains('active')) {
      event.preventDefault();
      this.close();
    }
  }

  onContainerClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Handle close button clicks via delegation so button listeners never go stale.
    if (target.closest('.sugar-cart-drawer__close')) {
      event.preventDefault();
      this.close();
      return;
    }

    // Close drawer when clicking outside the panel.
    const currentPanel = this.querySelector('.sugar-cart-drawer__panel');
    if (!currentPanel || !currentPanel.contains(target)) {
      this.close();
    }
  }

  open(triggeredBy) {
    this.refreshElementRefs();

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    // Prevent body scroll immediately even if the drawer is already open.
    document.body.classList.add('sugar-cart-drawer-open', 'overflow-hidden');

    // Keep open idempotent when multiple add-to-cart handlers call it.
    if (this.classList.contains('active')) {
      refreshSugarCartAnnouncements(this);
      return;
    }

    // Add document-level keyboard listener for Escape key
    document.removeEventListener('keydown', this.onKeyUp);
    document.addEventListener('keydown', this.onKeyUp);

    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    const finalizeOpen = () => {
      if (!this.classList.contains('active')) return;

      // Ensure sliders/layout measure correctly once the drawer is visible
      refreshSugarCartAnnouncements(this);

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.sugar-cart-drawer__empty')
        : this.querySelector('#SugarCartDrawer');

      const focusElement = this.querySelector('.sugar-cart-drawer__panel') ||
                          this.querySelector('.sugar-cart-drawer__close');

      if (typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    };

    const currentPanel = this.panel || this.querySelector('.sugar-cart-drawer__panel');
    if (currentPanel) {
      this.handleOpenTransitionEnd = (transitionEvent) => {
        if (transitionEvent.target !== currentPanel) return;

        finalizeOpen();

        if (this.openFinalizeTimeout) {
          clearTimeout(this.openFinalizeTimeout);
          this.openFinalizeTimeout = null;
        }

        if (this.handleOpenTransitionEnd) {
          this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
          this.handleOpenTransitionEnd = null;
        }
      };

      this.addEventListener('transitionend', this.handleOpenTransitionEnd);
    }

    // Fallback when transitionend is skipped due rapid DOM/state changes.
    this.openFinalizeTimeout = setTimeout(() => {
      finalizeOpen();
      this.openFinalizeTimeout = null;

      if (this.handleOpenTransitionEnd) {
        this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
        this.handleOpenTransitionEnd = null;
      }
    }, 380);

    // Trigger animation
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
        // Initialize slider early while panel slides in (element is visible and measurable now)
        requestAnimationFrame(() => refreshSugarCartAnnouncements(this));
      });
    } else {
      setTimeout(() => {
        this.classList.add('animate', 'active');
        setTimeout(() => refreshSugarCartAnnouncements(this));
      });
    }
  }

  close() {
    if (this.openFinalizeTimeout) {
      clearTimeout(this.openFinalizeTimeout);
      this.openFinalizeTimeout = null;
    }

    if (this.handleOpenTransitionEnd) {
      this.removeEventListener('transitionend', this.handleOpenTransitionEnd);
      this.handleOpenTransitionEnd = null;
    }

    this.classList.remove('active');

    // Remove document-level keyboard listener
    document.removeEventListener('keydown', this.onKeyUp);

    // Restore focus
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    // Restore body scroll
    document.body.classList.remove('sugar-cart-drawer-open', 'overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshElementRefs() {
    this.container = this.querySelector('#SugarCartDrawer');
    this.panel = this.querySelector('.sugar-cart-drawer__panel');
  }

  /**
   * Render updated cart contents from server response
   * @param {Object} parsedState - Response from cart API containing sections
   */
  renderContents(parsedState) {
    // Remove empty state if we have items
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble') return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHtml = parsedState.sections?.[section.id];

      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector
      );
    });

    this.refreshCartBubble(parsedState.sections);

    // Refresh cached element refs after DOM replacement
    setTimeout(() => {
      this.refreshElementRefs();

      // Note: cart items button listeners are set up by sugar-cart-items connectedCallback
      // Don't manually call setup methods here to avoid duplicate event listeners

      // Re-initialize enhancements after DOM replacement (scripts in injected HTML won't run)
      initSugarCartDrawerEnhancements(this);

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    if (!element) return '';

    // Extract style tags and append them to document head to ensure they're processed
    const styleTags = element.querySelectorAll('style');
    styleTags.forEach((style, index) => {
      // Generate a simple hash from the style content for identification
      const styleId = `sugar-cart-style-${index}`;

      // Remove old styles with same id if present
      const existingStyle = document.head.querySelector(`style[data-cart-drawer-style="${styleId}"]`);
      if (existingStyle) existingStyle.remove();

      const newStyle = document.createElement('style');
      newStyle.textContent = style.textContent;
      newStyle.setAttribute('data-cart-drawer-style', styleId);
      document.head.appendChild(newStyle);
      style.remove();
    });

    return element.innerHTML;
  }

  refreshCartBubble(sections) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    var html = sections && sections['cart-icon-bubble'];
    if (html !== undefined) {
      var doc = new DOMParser().parseFromString(html || '<div></div>', 'text/html');
      var bubble = doc.querySelector('.cart-count-bubble');
      target.innerHTML = bubble ? bubble.outerHTML : '';
      return;
    }

    var requestId = ++this.cartUpdateRequestId;
    var self = this;
    fetch(routes.cart_url + '?section_id=cart-icon-bubble')
      .then(function (r) { return r.text(); })
      .then(function (responseHtml) {
        if (requestId !== self.cartUpdateRequestId) return;
        var doc = new DOMParser().parseFromString(responseHtml, 'text/html');
        var bubble = doc.querySelector('.cart-count-bubble');
        target.innerHTML = bubble ? bubble.outerHTML : '';
      })
      .catch(function () {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'sugar-cart-drawer',
        selector: '#SugarCartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  /**
   * Handle cart update events from PubSub
   */
  onCartUpdate(event) {
    // Fetch updated section content
    const sectionId = this.sectionId || 'sugar-cart-drawer';
    const requestId = ++this.cartUpdateRequestId;

    fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== this.cartUpdateRequestId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Update cart items
        const sourceItemsWrapper = html.querySelector('sugar-cart-items');
        const targetItemsWrapper = this.querySelector('sugar-cart-items');
        const sourceItems = html.querySelector('.sugar-cart-drawer__items');
        const targetItems = this.querySelector('.sugar-cart-drawer__items');
        if (sourceItems && targetItems) {
          targetItems.innerHTML = sourceItems.innerHTML;
        } else if (!targetItemsWrapper && sourceItemsWrapper) {
          const panel = this.querySelector('.sugar-cart-drawer__panel');
          if (panel) {
            const emptyState = panel.querySelector('.sugar-cart-drawer__empty');
            if (emptyState) {
              panel.insertBefore(sourceItemsWrapper, emptyState);
            } else {
              panel.appendChild(sourceItemsWrapper);
            }
          }
        }

        // Update footer
        const sourceFooter = html.querySelector('.sugar-cart-drawer__footer');
        const targetFooter = this.querySelector('.sugar-cart-drawer__footer');
        if (sourceFooter && targetFooter) {
          const syncFooterBlock = (selector) => {
            const sourceBlock = sourceFooter.querySelector(selector);
            const targetBlock = targetFooter.querySelector(selector);

            if (sourceBlock && targetBlock) {
              targetBlock.innerHTML = sourceBlock.innerHTML;
              return;
            }

            if (sourceBlock && !targetBlock) {
              targetFooter.appendChild(sourceBlock.cloneNode(true));
              return;
            }

            if (!sourceBlock && targetBlock) {
              targetBlock.remove();
            }
          };

          // Keep the cart toggles node mounted; only update dynamic footer blocks.
          syncFooterBlock('.sugar-cart-subtotals');
          syncFooterBlock('.sugar-cart-checkout');
          syncFooterBlock('.payment-badges');
          syncFooterBlock('.icon-text-list');
        } else if (!targetFooter && sourceFooter) {
          const panel = this.querySelector('.sugar-cart-drawer__panel');
          if (panel) {
            panel.appendChild(sourceFooter);
          }
        }

        // Update progress bar if present
        const sourceProgress = html.querySelector('.sugar-cart-progress');
        const targetProgress = this.querySelector('.sugar-cart-progress');
        if (sourceProgress && targetProgress) {
          targetProgress.outerHTML = sourceProgress.outerHTML;
        }

        // Update header count
        const sourceHeader = html.querySelector('.sugar-cart-header');
        const targetHeader = this.querySelector('.sugar-cart-header');
        if (sourceHeader && targetHeader) {
          targetHeader.innerHTML = sourceHeader.innerHTML;
        }

        // Update empty state
        const sourceEmpty = html.querySelector('.sugar-cart-drawer');
        if (sourceEmpty) {
          const isEmpty = sourceEmpty.classList.contains('is-empty');
          this.classList.toggle('is-empty', isEmpty);

          if (!isEmpty) {
            const currentEmpty = this.querySelector('.sugar-cart-drawer__empty');
            currentEmpty && currentEmpty.remove();
          }
        }

        this.refreshElementRefs();
        this.refreshCartBubble();
      })
      .catch((error) => {
        if (requestId !== this.cartUpdateRequestId) return;
        console.error('Error updating cart drawer:', error);
      });
  }
}

customElements.define('sugar-cart-drawer', SugarCartDrawer);

function initSugarCartDrawerEnhancements(rootEl) {
  if (!rootEl || !(rootEl instanceof HTMLElement)) return;
  // Don't initialize announcements slider here - wait until drawer is visible
  initSugarCartDrawerCountdownTimers(rootEl);
  initSugarCartSophisticatedCountdowns(rootEl);
}

function refreshSugarCartAnnouncements(rootEl) {
  if (!rootEl || !(rootEl instanceof HTMLElement)) return;

  const containers = rootEl.querySelectorAll('[data-sugar-cart-announcements]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const splideEl = container.querySelector('[data-sugar-cart-announcements-splide]');
    if (!splideEl) return;

    // If already initialized, do nothing (don't refresh - it causes jitter)
    if (splideEl._splideInstance || splideEl._splideInitializing) {
      return;
    }

    // Mark as initializing to prevent duplicate attempts
    splideEl._splideInitializing = true;

    // Initialize for the first time (drawer is now visible)
    initSugarCartAnnouncements(rootEl);
  });
}

function initSugarCartAnnouncements(rootEl, attempt = 0) {
  const containers = rootEl.querySelectorAll('[data-sugar-cart-announcements]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const enableSlider = container.dataset.enableSlider === 'true';
    const splideEl = container.querySelector('[data-sugar-cart-announcements-splide]');
    if (!enableSlider || !splideEl) return;

    // Skip if already initialized
    if (splideEl._splideInstance) return;

    // Wait for Splide to load (it may be loaded after this file due to defer ordering)
    if (typeof Splide === 'undefined') {
      if (attempt < 40) {
        setTimeout(() => initSugarCartAnnouncements(rootEl, attempt + 1), 50);
      }
      return;
    }

    // Ensure required classes exist for Splide.
    // Only tag <li> children as slides — integrity guards inject sibling
    // <style>/<script> nodes which Splide would otherwise render as visible
    // "slides", surfacing their source text inside the cart drawer.
    const listEl = splideEl.querySelector('.splide__list');
    if (listEl) {
      Array.from(listEl.children).forEach((child) => {
        if (child && child.tagName === 'LI' && child.classList) {
          child.classList.add('splide__slide');
        }
      });
    }

    const slideCount = parseInt(container.dataset.slideCount || '0', 10) || 0;
    const autoplay = container.dataset.autoplay === 'true';
    const autoplaySpeed = (parseInt(container.dataset.autoplaySpeed || '5', 10) || 5) * 1000;
    const pauseOnHover = container.dataset.pauseOnHover === 'true';
    const showArrows = container.dataset.showArrows === 'true';
    const arrowColorMode = container.dataset.arrowColor || 'auto';

    const options = {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      pagination: false,
      arrows: showArrows && slideCount > 1,
      autoplay: autoplay && slideCount > 1,
      interval: autoplaySpeed,
      pauseOnHover: pauseOnHover,
      pauseOnFocus: true,
      drag: slideCount > 1,
      speed: 400,
      easing: 'ease',
      gap: 0,
      width: '100%',
      fixedWidth: false,
      autoWidth: false
    };

    const splide = new Splide(splideEl, options);

    function updateArrowColor() {
      if (arrowColorMode !== 'auto') return;

      const slideObj = splide.Components?.Slides?.getAt(splide.index);
      const activeSlide = slideObj?.slide || container.querySelector('.splide__slide.is-active');
      const textColor = activeSlide?.dataset?.textColor;

      if (textColor) {
        container.style.setProperty('--sugar-cart-announcements-arrow-color', textColor);
      }
    }

    const trackEl = splideEl.querySelector('.splide__track');

    function updateTrackBackground(nextIndex) {
      if (!trackEl) return;

      const index = typeof nextIndex === 'number' ? nextIndex : splide.index;
      const slideObj = splide.Components?.Slides?.getAt(index);
      const slide = slideObj?.slide;
      // Support both announcement items and countdown slides
      const inner = slide?.querySelector('.sugar-cart-announcement-item__inner') ||
                    slide?.querySelector('.sugar-cart-announcement-countdown__inner') ||
                    slide?.querySelector('.sugar-cart-announcement-rotating-text__inner');

      if (inner) {
        const bg = getComputedStyle(inner).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          trackEl.style.backgroundColor = bg;
        }
      }
    }

    splide.on('mounted', () => updateTrackBackground());
    splide.on('move', (newIndex) => updateTrackBackground(newIndex));

    if (arrowColorMode === 'auto') {
      splide.on('mounted', updateArrowColor);
      splide.on('move', updateArrowColor);
      splide.on('updated', updateArrowColor);
    }

    splide.mount();
    splideEl._splideInstance = splide;

    // Refresh after a short delay to ensure proper loop initialization
    requestAnimationFrame(() => {
      if (splideEl._splideInstance) {
        splideEl._splideInstance.refresh();
      }
    });
  });
}

function initSugarCartDrawerCountdownTimers(rootEl) {
  // One interval for the entire cart drawer to avoid leaks on re-render.
  if (window.__sugarCartDrawerCountdownIntervalId) {
    clearInterval(window.__sugarCartDrawerCountdownIntervalId);
    window.__sugarCartDrawerCountdownIntervalId = null;
  }

  const timers = Array.from(rootEl.querySelectorAll('[data-countdown-timer]'));
  if (!timers.length) return;

  const timerStates = timers.map((timer) => {
    const hoursEl = timer.querySelector('[data-countdown-hours]');
    const minutesEl = timer.querySelector('[data-countdown-minutes]');
    const secondsEl = timer.querySelector('[data-countdown-seconds]');

    const initialHours = parseInt(timer.dataset.initialHours, 10) || 0;
    const initialMinutes = parseInt(timer.dataset.initialMinutes, 10) || 0;
    const initialSeconds = parseInt(timer.dataset.initialSeconds, 10) || 0;
    const shouldRestart = timer.dataset.restart === 'restart';

    let totalSeconds = (initialHours * 3600) + (initialMinutes * 60) + initialSeconds;
    const originalTotal = totalSeconds;

    function updateDisplay() {
      const hrs = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hoursEl) hoursEl.textContent = String(hrs).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(mins).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(secs).padStart(2, '0');
    }

    updateDisplay();

    return {
      tick: () => {
        if (totalSeconds > 0) {
          totalSeconds -= 1;
          updateDisplay();
          return;
        }
        if (shouldRestart) {
          totalSeconds = originalTotal;
          updateDisplay();
        }
      }
    };
  });

  window.__sugarCartDrawerCountdownIntervalId = setInterval(() => {
    timerStates.forEach((state) => state.tick());
  }, 1000);
}

function initSugarCartSophisticatedCountdowns(rootEl) {
  // Clear any existing interval for sophisticated countdowns
  if (window.__sugarCartSophisticatedCountdownIntervalId) {
    clearInterval(window.__sugarCartSophisticatedCountdownIntervalId);
    window.__sugarCartSophisticatedCountdownIntervalId = null;
  }

  // Pin the host element so each interval tick re-queries from the current cart drawer DOM.
  // After add-to-cart, #SugarCartDrawer.innerHTML is swapped wholesale; capturing element
  // references at init time leaves us writing into detached <span>s while the visible spans
  // keep the SSR "00" default. Re-querying on every tick is cheap and keeps the timer in
  // sync regardless of how many times the drawer's contents are replaced.
  const host = rootEl || document.querySelector('sugar-cart-drawer') || document;

  function pad(num) {
    return String(num).padStart(2, '0');
  }

  function parseDateParts(str) {
    if (!str) return null;
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return null;
    return { y: +m[1], mo: +m[2] - 1, d: +m[3], h: +m[4], mi: +m[5], s: +(m[6] || 0) };
  }

  // Per-timer state keyed by the element itself so auto_renew end time persists
  // across ticks even though we don't keep the timer node in a closure.
  const stateByTimer = new WeakMap();

  function getState(timer) {
    let state = stateByTimer.get(timer);
    if (!state) {
      state = { autoRenewEndTime: null };
      stateByTimer.set(timer, state);
    }
    return state;
  }

  function getTargetDate(timer) {
    const state = getState(timer);
    const type = timer.dataset.countdownType;
    const timezoneMode = timer.dataset.timezoneMode;
    const utcOffset = parseInt(timer.dataset.utcOffset, 10) || 0;

    if (type === 'specific_date') {
      const p = parseDateParts(timer.dataset.endDate);
      if (!p) return null;

      let targetDate;
      if (timezoneMode === 'fixed') {
        targetDate = new Date(Date.UTC(p.y, p.mo, p.d, p.h, p.mi, p.s) - utcOffset * 3600000);
      } else {
        targetDate = new Date(p.y, p.mo, p.d, p.h, p.mi, p.s);
      }
      if (isNaN(targetDate.getTime())) return null;
      return targetDate;
    }

    if (type === 'every_24_hours') {
      const now = new Date();
      let midnight;

      if (timezoneMode === 'fixed') {
        const utcMidnight = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0)
        );
        midnight = new Date(utcMidnight.getTime() - utcOffset * 60 * 60 * 1000);
      } else {
        midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      }
      return midnight;
    }

    if (type === 'auto_renew') {
      if (!state.autoRenewEndTime) {
        const autoDays = parseInt(timer.dataset.autoDays, 10) || 0;
        const autoHours = parseInt(timer.dataset.autoHours, 10) || 0;
        const autoMinutes = parseInt(timer.dataset.autoMinutes, 10) || 0;
        const autoSeconds = parseInt(timer.dataset.autoSeconds, 10) || 0;
        const totalMs =
          autoDays * 24 * 60 * 60 * 1000 +
          autoHours * 60 * 60 * 1000 +
          autoMinutes * 60 * 1000 +
          autoSeconds * 1000;
        state.autoRenewEndTime = new Date(Date.now() + totalMs);
      }
      return state.autoRenewEndTime;
    }

    return null;
  }

  function updateDisplay(timer, days, hours, minutes, seconds) {
    const daysEl = timer.querySelector('[data-days]');
    const hoursEl = timer.querySelector('[data-hours]');
    const minutesEl = timer.querySelector('[data-minutes]');
    const secondsEl = timer.querySelector('[data-seconds]');
    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }

  function tickOne(timer) {
    // Skip detached timers — happens briefly while #SugarCartDrawer.innerHTML is being swapped.
    if (!timer.isConnected) return;

    const type = timer.dataset.countdownType;
    const target = getTargetDate(timer);
    if (!target) {
      updateDisplay(timer, 0, 0, 0, 0);
      return;
    }

    const now = new Date();
    let diff = target - now;

    if (diff <= 0) {
      if (type === 'specific_date') {
        timer.classList.add('countdown-timer--ended');
        return;
      } else if (type === 'every_24_hours' || type === 'auto_renew') {
        getState(timer).autoRenewEndTime = null;
        tickOne(timer);
        return;
      }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * 1000 * 60 * 60 * 24;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    diff -= hours * 1000 * 60 * 60;
    const minutes = Math.floor(diff / (1000 * 60));
    diff -= minutes * 1000 * 60;
    const seconds = Math.floor(diff / 1000);

    updateDisplay(timer, days, hours, minutes, seconds);
  }

  function tickAll() {
    const timers = host.querySelectorAll
      ? host.querySelectorAll('[data-sophisticated-countdown]')
      : [];
    if (!timers.length) return;
    timers.forEach(tickOne);
  }

  // Run immediately so the visible spans flip from "00" to their real values within the
  // same frame the drawer slides in (not on the next 1-second tick).
  tickAll();

  window.__sugarCartSophisticatedCountdownIntervalId = setInterval(tickAll, 1000);
}


/**
 * Sugar Cart Items
 * Handles cart item quantity updates and removals
 */
class SugarCartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('SugarCartDrawer-LineItemStatus');
    this.sectionId = this.dataset.sectionId;

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // Quantity/remove clicks are delegated at WINDOW capture, not bound per
    // button and not delegated from this element: cart apps clone/replace the
    // item buttons (stripping per-button listeners) AND some hijack clicks
    // with document-capture handlers of their own (turning a quantity click
    // into an add-to-cart + /cart navigation). Window capture fires before
    // document capture, so we see our buttons' clicks first, handle them, and
    // stop the event from reaching anything else.
    this.windowClickHandler = (event) => this.onDelegatedClick(event);
    window.addEventListener('click', this.windowClickHandler, true);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Skip if this component triggered the update, or if product-form already handled rendering via renderContents
      if (event.source === 'sugar-cart-items' || event.source === 'product-form') return;
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.windowClickHandler) {
      window.removeEventListener('click', this.windowClickHandler, true);
    }
  }

  onDelegatedClick(event) {
    const target = event.target;
    if (!target || !target.closest) return;

    const removeButton = target.closest('.sugar-cart-item__remove');
    if (removeButton && this.contains(removeButton)) {
      if (removeButton.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.updateQuantity(removeButton.dataset.index, 0, event, undefined, undefined, removeButton.dataset.key);
      return;
    }

    const quantityButton = target.closest('.sugar-cart-item__quantity-btn');
    if (!quantityButton || !this.contains(quantityButton) || quantityButton.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const quantityWrapper = quantityButton.closest('.sugar-cart-item__quantity');
    const input = quantityWrapper && quantityWrapper.querySelector('.sugar-cart-item__quantity-input');
    if (!input) return;

    const currentValue = parseInt(input.value, 10);
    const step = parseInt(input.step, 10) || 1;
    const min = parseInt(input.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;

    let newValue;
    if (quantityButton.name === 'plus') {
      newValue = Math.min(currentValue + step, max);
    } else {
      newValue = Math.max(currentValue - step, min);
    }

    if (newValue !== currentValue) {
      input.value = newValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onChange(event) {
    const input = event.target;
    if (!input.classList.contains('sugar-cart-item__quantity-input')) return;

    const index = input.dataset.index;
    const key = input.dataset.key;
    const value = parseInt(input.value, 10);

    this.updateQuantity(index, value, event, input.name, input.dataset.quantityVariantId, key);
  }

  onCartUpdate() {
    const sectionId = this.sectionId || 'sugar-cart-drawer';

    return fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceItems = html.querySelector('sugar-cart-items');
        if (sourceItems) {
          this.innerHTML = sourceItems.innerHTML;
        }
      })
      .catch((error) => {
        console.error('Error updating cart items:', error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'SugarCartDrawer',
        section: this.sectionId || 'sugar-cart-drawer',
        selector: '.sugar-cart-drawer__panel',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  isAutoAddedCartItem(item) {
    const properties = item?.properties || {};
    const hasOfferGiftFlag = Object.prototype.hasOwnProperty.call(properties, '_offer_gift');
    const hasProgressFlag = Object.prototype.hasOwnProperty.call(properties, '_sugar_progress_goal');

    return (
      properties._gift_list === 'true' ||
      properties._gift_unlock === 'true' ||
      hasOfferGiftFlag ||
      hasProgressFlag ||
      properties._cart_toggle === 'true' ||
      properties._cart_upsell === 'true'
    );
  }

  isProductBoundAutoGift(item) {
    const properties = item?.properties || {};
    return (
      properties._gift_list === 'true' ||
      properties._gift_unlock === 'true' ||
      Object.prototype.hasOwnProperty.call(properties, '_offer_gift')
    );
  }

  getOrphanedAutoGiftKeys(cartState) {
    if (!cartState || !Array.isArray(cartState.items)) return [];

    const qualifyingProductQuantities = new Map();
    let hasNonAutoItems = false;
    cartState.items.forEach((item) => {
      if (!this.isAutoAddedCartItem(item)) {
        hasNonAutoItems = true;
        const productId = String(item.product_id);
        const quantity = parseInt(item.quantity, 10) || 0;
        const existingQty = qualifyingProductQuantities.get(productId) || 0;
        qualifyingProductQuantities.set(productId, existingQty + quantity);
      }
    });

    const orphanedGiftKeys = [];
    cartState.items.forEach((item) => {
      if (!this.isProductBoundAutoGift(item)) return;

      const properties = item?.properties || {};
      const qualifyingProductId = String(properties._gift_for_product || '').trim();
      const qualifyingQty = qualifyingProductQuantities.get(qualifyingProductId) || 0;

      if (!qualifyingProductId || qualifyingQty <= 0) {
        if (item.key) orphanedGiftKeys.push(item.key);
        return;
      }

      // Gift Unlocks tiers can require a minimum qualifying quantity.
      if (properties._gift_unlock === 'true') {
        const requiredQty = parseInt(properties._gift_unlock_required_qty, 10);
        if (Number.isFinite(requiredQty) && requiredQty > 0 && qualifyingQty < requiredQty) {
          if (item.key) orphanedGiftKeys.push(item.key);
        }
      }
    });

    // If there are no regular cart items left, toggle-driven add-ons should be removed
    // so the drawer can transition to a true empty-cart state.
    if (!hasNonAutoItems) {
      cartState.items.forEach((item) => {
        const properties = item?.properties || {};
        if (
          item.key &&
          (properties._cart_toggle === 'true' || properties._cart_upsell === 'true')
        ) {
          orphanedGiftKeys.push(item.key);
        }
      });
    }

    return [...new Set(orphanedGiftKeys)];
  }

  async removeOrphanedAutoGifts(cartState) {
    const orphanedGiftKeys = this.getOrphanedAutoGiftKeys(cartState);
    if (orphanedGiftKeys.length === 0) return null;

    const updates = {};
    orphanedGiftKeys.forEach((key) => {
      updates[key] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const updateRoute = routes.cart_update_url || '/cart/update';

    try {
      const response = await fetch(updateRoute, { ...fetchConfig(), body });
      const state = await response.text();
      return JSON.parse(state);
    } catch (error) {
      console.error('Error removing orphaned auto gifts:', error);
      return null;
    }
  }

  sugarTrackQtyIncrease(item, delta) {
    const entity = item && item.product_id != null ? String(item.product_id) : '';
    if (!entity || !delta || delta < 1) return;
    const ctx = {
      path: window.location.pathname,
      product: item.handle || '',
      productTitle: item.product_title || item.title || '',
      section: 'cart_qty'
    };
    const args = ['atc', 'cart_qty', entity, '+' + delta, ctx];
    if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
    else (window._sugarQ = window._sugarQ || []).push(args);
  }

  updateQuantity(line, quantity, event, name, variantId, lineItemKey) {
    const isRemoval = quantity === 0;

    // Capture the pre-change quantity so we can count a genuine increase.
    let sugarOldQty = 0;
    try {
      const qInput = this.querySelector(`[data-index="${line}"]`);
      if (qInput) sugarOldQty = parseInt(qInput.getAttribute('value'), 10) || 0;
    } catch (e) {}

    this.enableLoading(line);
    this.clearLiveRegions();

    const payload = {
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };
    if (lineItemKey) {
      payload.id = lineItemKey;
    } else {
      payload.line = line;
    }
    const body = JSON.stringify(payload);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then(async (state) => {
        let parsedState = JSON.parse(state);

        if (parsedState.errors) {
          // Handle errors
          const input = this.querySelector(`[data-index="${line}"]`);
          if (input) {
            input.value = input.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const stateAfterGiftCleanup = await this.removeOrphanedAutoGifts(parsedState);
        if (stateAfterGiftCleanup) {
          parsedState = stateAfterGiftCleanup;
        }

        // Update empty state
        this.classList.toggle('is-empty', parsedState.item_count === 0);

        const cartDrawer = document.querySelector('sugar-cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        // Re-render sections
        this.getSectionsToRender().forEach((section) => {
          if (section.id === 'cart-icon-bubble') return;

          const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) ||
                                   document.getElementById(section.id);

          if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (cartDrawer && typeof cartDrawer.refreshCartBubble === 'function') {
          cartDrawer.refreshCartBubble(parsedState.sections);
        }

        // Re-initialize enhancements (announcements slider, countdown timers)
        if (cartDrawer) {
          initSugarCartDrawerEnhancements(cartDrawer);
          // Announcements slider needs explicit init since drawer is already visible
          initSugarCartAnnouncements(cartDrawer);
        }

        // Update live regions.
        //
        // Surface the "you can only add N" notice only when Shopify actually
        // delivered FEWER units than we requested for the line we changed (a real
        // inventory / quantity-rule cap).
        //
        // Locate the changed line by its line-item KEY, never by positional index.
        // When a variant occupies more than one cart line — e.g. a discount or
        // free-gift offer splits it into two line items — the line-item array can be
        // re-ordered, merged or re-split, and concurrent cart mutations from the
        // offer/gift system can shift positions, around this very update. The old
        // check compared a positional `items[line - 1]` against a `data-index` read
        // on the (now detached) cart element, so it pointed at the wrong line and
        // fired a false "max quantity" error even though the change applied cleanly.
        const changedItem = lineItemKey
          ? parsedState.items.find((cartItem) => cartItem.key === lineItemKey)
          : parsedState.items[line - 1];
        const deliveredQuantity = changedItem?.quantity;

        // Sugar Analytics: count a genuine quantity increase (Shopify actually
        // delivered more units than the line had before) as a cart-add action.
        if (
          !isRemoval &&
          changedItem &&
          typeof deliveredQuantity === 'number' &&
          deliveredQuantity > sugarOldQty
        ) {
          this.sugarTrackQtyIncrease(changedItem, deliveredQuantity - sugarOldQty);
        }

        let message = '';
        if (
          !isRemoval &&
          typeof deliveredQuantity === 'number' &&
          deliveredQuantity < quantity
        ) {
          message = window.cartStrings.quantityError.replace('[quantity]', deliveredQuantity);
        }

        this.updateLiveRegions(line, message);

        // Publish update event
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sugar-cart-items',
          cartData: parsedState,
          variantId: variantId
        });
      })
      .catch((error) => {
        console.error('Error updating quantity:', error);
        this.querySelectorAll('.sugar-cart-item__loading-spinner').forEach((spinner) => {
          spinner.style.display = 'none';
        });
        const errors = document.getElementById('SugarCartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  clearLiveRegions() {
    document.querySelectorAll('.sugar-cart-item__error').forEach((lineItemError) => {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = '';
      }
      lineItemError.classList.remove('is-active');
    });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`SugarCartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      lineItemError.classList.toggle('is-active', Boolean(message));
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
    }

    const cartStatus = document.getElementById('SugarCartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  enableLoading(line) {
    const item = this.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.add('is-loading');
    }

    const cartItems = document.getElementById('SugarCartDrawer-CartItems');
    if (cartItems) {
      cartItems.classList.add('cart__items--disabled');
    }

    document.activeElement?.blur();

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
    }
  }

  disableLoading(line) {
    const item = this.querySelector(`[data-line-index="${line}"]`);
    if (item) {
      item.classList.remove('is-loading');
    }

    const cartItems = document.getElementById('SugarCartDrawer-CartItems');
    if (cartItems) {
      cartItems.classList.remove('cart__items--disabled');
    }
  }
}

customElements.define('sugar-cart-items', SugarCartItems);


/**
 * Sugar Cart Progress
 * Handles the progress bar with live updates
 */
class SugarCartProgress extends HTMLElement {
  constructor() {
    super();
    this.goals = [];
    this.parseGoals();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.updateProgress(event.cartData, event.source);
    });

    // Sync initial bar widths from live cart data so mode toggles apply immediately.
    this.initialProgressSync();

    // Sugar Analytics: fire a "bar shown" impression once it's actually visible.
    this.setupSugarImpression();
  }

  async initialProgressSync() {
    try {
      const cartData = await this.fetchCart();
      this.updateProgress(cartData, 'initial-sync');
    } catch (e) {
      // Silent fail on initial sync
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this._sugarImpObserver) {
      this._sugarImpObserver.disconnect();
      this._sugarImpObserver = null;
    }
  }

  /* ── Sugar Analytics (cart_progress goal-reach) ────────────────────── */
  sugarTrack(type, detail, label) {
    const entity = this.getAttribute('data-sugar-entity');
    if (!entity) return;
    const ctx = { path: window.location.pathname, section: entity, label: label || '' };
    const args = [type, 'cart_progress', entity, detail || '', ctx];
    if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
    else (window._sugarQ = window._sugarQ || []).push(args);
  }

  fireSugarImpression() {
    const entity = this.getAttribute('data-sugar-entity');
    if (!entity) return;
    let first = true;
    try {
      const key = '_sugar_imp_' + entity;
      if (sessionStorage.getItem(key)) first = false;
      else sessionStorage.setItem(key, '1');
    } catch (e) {}
    if (first) this.sugarTrack('impression', '', '');
  }

  setupSugarImpression() {
    const entity = this.getAttribute('data-sugar-entity');
    if (!entity) return;
    try {
      if (sessionStorage.getItem('_sugar_imp_' + entity)) return;
    } catch (e) {}
    if (typeof IntersectionObserver !== 'function') {
      this.fireSugarImpression();
      return;
    }
    this._sugarImpObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.fireSugarImpression();
          if (this._sugarImpObserver) {
            this._sugarImpObserver.disconnect();
            this._sugarImpObserver = null;
          }
          break;
        }
      }
    }, { threshold: 0.5 });
    this._sugarImpObserver.observe(this);
  }

  parseGoals() {
    // Parse goal data from data attributes
    // Note: data-goal-1-target becomes dataset['goal-1Target'] (hyphen before number stays)
    const goalCount = parseInt(this.dataset.goalCount, 10) || 1;
    this.fillGoalsIndependently = this.dataset.fillGoalsIndependently === 'true';
    this.autoAddProperty = '_sugar_progress_goal';
    this.processing = false;

    for (let i = 1; i <= goalCount; i++) {
      const type = this.dataset[`goal-${i}Type`];
      const price = parseFloat(this.dataset[`goal-${i}Price`]) || 0;
      const quantity = parseInt(this.dataset[`goal-${i}Quantity`], 10) || 0;
      const variantId = this.dataset[`goal-${i}Variant`];
      const autocompleteSub = this.dataset[`goal-${i}AutocompleteSub`] === 'true';
      const target = parseInt(this.dataset[`goal-${i}Target`], 10) || 0;

      this.goals.push({
        type,
        threshold: target || (type === 'price' ? price * 100 : quantity),
        target: target || (type === 'price' ? price * 100 : quantity),
        reached: false,
        variantId: variantId ? parseInt(variantId, 10) : null,
        autocompleteSub,
        goalNumber: i,
        label: this.getAttribute('data-sugar-goal-' + i + '-label') || ''
      });
    }
  }

  updateProgress(cartData, source) {
    if (!cartData) return;

    const cartTotal = (cartData.items_subtotal_price ?? cartData.total_price);
    const cartItemCount = cartData.item_count;
    const hasSubscription = Array.isArray(cartData.items)
      ? cartData.items.some((item) => item.selling_plan_allocation)
      : false;
    const fillGoalsIndependently = (this.dataset.fillGoalsIndependently || '')
      .toString()
      .trim()
      .toLowerCase() === 'true';

    const hasHigherAutocomplete = (goalNum) => {
      return this.goals.some((g) => g.goalNumber > goalNum && g.autocompleteSub && hasSubscription);
    };

    const isAutocompleteReached = (goal) => {
      return hasSubscription && (goal.autocompleteSub || hasHigherAutocomplete(goal.goalNumber));
    };

    // Calculate reached state first so sequential mode can gate later goals.
    this.goals.forEach((goal, index) => {
      const currentValue = goal.type === 'price' ? cartTotal : cartItemCount;
      goal.reached = currentValue >= goal.threshold || isAutocompleteReached(goal);
    });

    // Sugar Analytics: fire a goal-reached event the first time each goal is
    // met this session — fires on every cart update (drawer open or not), so a
    // threshold crossed before the cart is opened still counts. Deduped per
    // goal via sessionStorage.
    const sugarEntity = this.getAttribute('data-sugar-entity');
    if (sugarEntity) {
      this.goals.forEach((goal) => {
        if (!goal.reached) return;
        let firstTime = true;
        try {
          const key = '_sugar_cprog_' + sugarEntity + '_' + goal.goalNumber;
          if (sessionStorage.getItem(key)) firstTime = false;
          else sessionStorage.setItem(key, '1');
        } catch (e) {}
        if (firstTime) this.sugarTrack('convert', 'unlocked:' + goal.goalNumber, goal.label);
      });
    }

    // Calculate progress for each goal
    this.goals.forEach((goal, index) => {
      const currentValue = goal.type === 'price' ? cartTotal : cartItemCount;
      const goalAutocompleteReached = isAutocompleteReached(goal);
      let progress = 0;

      if (goalAutocompleteReached) {
        progress = 100;
      } else if (index === 0) {
        progress = goal.threshold > 0 ? (currentValue / goal.threshold) * 100 : 100;
      } else if (fillGoalsIndependently) {
        progress = goal.threshold > 0 ? (currentValue / goal.threshold) * 100 : 100;
      } else {
        const previousGoal = this.goals[index - 1];
        if (!previousGoal.reached) {
          progress = 0;
        } else {
          const segmentStart = goal.type === previousGoal.type ? previousGoal.threshold : 0;
          if (currentValue <= segmentStart) {
            progress = 0;
          } else if (currentValue >= goal.threshold) {
            progress = 100;
          } else {
            const segmentRange = goal.threshold - segmentStart;
            progress = segmentRange > 0
              ? ((currentValue - segmentStart) / segmentRange) * 100
              : 100;
          }
        }
      }

      if (!Number.isFinite(progress)) progress = 0;
      progress = Math.max(0, Math.min(progress, 100));

      // Update progress bar UI
      const bar = this.querySelector(`.sugar-cart-progress__bar[data-goal="${index + 1}"], [data-goal="${index + 1}"]`);
      if (bar) {
        bar.style.width = `${progress}%`;
      }

      // Update milestone state
      const milestone = this.querySelector(`.sugar-cart-progress__milestone[data-goal="${index + 1}"]`);
      if (milestone) {
        milestone.classList.toggle('sugar-cart-progress__milestone--reached', goal.reached);
      }
    });

    // Update text with placeholders
    this.updateProgressText(cartData);

    // Handle auto-add/remove products
    this.handleAutoAddProducts(cartData, source);
  }

  updateProgressText(cartData) {
    const textElement = this.querySelector('.sugar-cart-progress__text');
    if (!textElement) return;

    // Find the first unreached goal
    const currentGoal = this.goals.find(g => !g.reached) || this.goals[this.goals.length - 1];
    if (!currentGoal) return;

    const goalIndex = this.goals.indexOf(currentGoal);

    // Calculate remaining
    const currentValue = currentGoal.type === 'price'
      ? (cartData.items_subtotal_price ?? cartData.total_price)
      : cartData.item_count;

    const remaining = Math.max(0, currentGoal.threshold - currentValue);

    // Format remaining amount
    let remainingText;
    if (currentGoal.type === 'price') {
      // Use Shopify.formatMoney if available, otherwise fallback to simple format
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        remainingText = Shopify.formatMoney(remaining);
      } else {
        remainingText = '$' + (remaining / 100).toFixed(2);
      }
    } else {
      remainingText = remaining.toString();
    }

    // Update placeholder text (handled server-side in Liquid)
    // This is for client-side updates after cart changes
  }

  async handleAutoAddProducts(cartData, source) {
    // Skip if this update came from our own add/remove operations
    if (source === 'sugar-cart-progress') return;

    // Check if any goals have auto-add products configured
    const goalsWithProducts = this.goals.filter(g => g.variantId);
    if (goalsWithProducts.length === 0 || this.processing) return;

    this.processing = true;

    try {
      // Check if cart has any subscription items
      const hasSubscription = cartData.items.some(item => item.selling_plan_allocation);

      // Calculate qualifying totals excluding progress auto-added items.
      // Keep both final/original totals so remove checks stay stable even when
      // cart-level discounts re-allocate after the auto gift is added.
      let adjustedFinalTotal = 0;
      let adjustedOriginalTotal = 0;
      let adjustedItemCount = 0;

      cartData.items.forEach((item) => {
        const properties = item?.properties || {};
        const isProgressAutoAdded = Object.prototype.hasOwnProperty.call(
          properties,
          this.autoAddProperty
        );
        if (isProgressAutoAdded) return;

        adjustedFinalTotal += parseInt(item.final_line_price, 10) || 0;
        adjustedOriginalTotal += parseInt(item.original_line_price, 10) || 0;
        adjustedItemCount += parseInt(item.quantity, 10) || 0;
      });

      // Check if any higher goal has autocomplete enabled (for cascade logic)
      const hasHigherAutocomplete = (goalNum) => {
        return this.goals.some(g => g.goalNumber > goalNum && g.autocompleteSub && hasSubscription);
      };

      for (const goal of goalsWithProducts) {
        const autoAddedItem = cartData.items.find(item =>
          item.variant_id === goal.variantId &&
          item.properties &&
          item.properties[this.autoAddProperty] === String(goal.goalNumber)
        );

        const regularItem = cartData.items.find(item =>
          item.variant_id === goal.variantId &&
          (!item.properties || !item.properties[this.autoAddProperty])
        );

        const addCurrent = goal.type === 'price' ? adjustedFinalTotal : adjustedItemCount;
        const keepCurrent = goal.type === 'price'
          ? Math.max(adjustedFinalTotal, adjustedOriginalTotal)
          : adjustedItemCount;

        // When the auto gift is already in cart, use the stable "keep" value to avoid
        // oscillation from discount re-allocation triggered by the gift line itself.
        const goalCurrent = autoAddedItem ? keepCurrent : addCurrent;
        // Goal is reached if threshold met OR subscription autocomplete active (including cascade)
        const isReached = goalCurrent >= goal.target ||
          (hasSubscription && (goal.autocompleteSub || hasHigherAutocomplete(goal.goalNumber)));

        if (isReached && !autoAddedItem && !regularItem) {
          await this.addProductToCart(goal.variantId, goal.goalNumber);
        } else if (!isReached && autoAddedItem) {
          await this.removeProductFromCart(autoAddedItem.key);
        }
      }
    } catch (e) {
      console.error('Sugar Cart Progress auto-add error:', e);
    } finally {
      this.processing = false;
    }
  }

  async addProductToCart(variantId, goalNumber) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            [this.autoAddProperty]: String(goalNumber)
          }
        }]
      })
    });

    if (response.ok) {
      // Fetch fresh cart data and trigger refresh
      const cartData = await this.fetchCart();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sugar-cart-progress', cartData });
    }
  }

  async removeProductFromCart(lineItemKey) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: 0
      })
    });

    if (response.ok) {
      // Fetch fresh cart data and trigger refresh
      const cartData = await this.fetchCart();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sugar-cart-progress', cartData });
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }
}

customElements.define('sugar-cart-progress', SugarCartProgress);
